import React, { useState } from "react";

import { TFunction, formatNumber, useParams, useTranslation } from "utils";

import {
  CarbonProgram,
  CarbonProgramOutcomesResponse,
  EVALUATION_METRICS,
  FieldEvaluationMetricCorrectionsResponse,
  IMPACT_PANEL_CALCULATION_TYPE,
  VIEW_TYPE,
} from "models";

import { AlertBanner, ExtraInfo, Flex, Icon, IconLinkButton, Option, Select, Typography } from "components";

import { FieldTimelineLineChartWithRanges } from "pages/app/field-timeline/components/FieldTimelineLineChartWithRanges";
import {
  calculateMetricChangeFromBaseline,
  formatChangeString,
  getAvailableEvaluationMetrics,
  getAvailableYearsByEvaluationMetric,
  getEvaluationMetricAllYearsLabel,
  getEvaluationMetricDatasets,
  getEvaluationMetricsUnitOfMeasure,
} from "pages/app/field-timeline/utils/EvaluationMetricUtils";

type EvaluationMetricsPanelProps = {
  selectedMetric: EVALUATION_METRICS;
  carbonProgramOutcomes: CarbonProgramOutcomesResponse;
  reportingYear: number;
  fieldSizes: Record<string, number>;
  viewType: VIEW_TYPE;
  existingCorrectionsByFieldId: FieldEvaluationMetricCorrectionsResponse;
  reportingPeriod?: CarbonProgram["fieldsManagement"]["reportingPeriod"];
};

export const EvaluationMetricsPanel = ({
  selectedMetric,
  carbonProgramOutcomes,
  reportingYear,
  fieldSizes,
  viewType,
  existingCorrectionsByFieldId,
  reportingPeriod,
}: EvaluationMetricsPanelProps) => {
  const { t } = useTranslation(["fieldTimeline"]);
  const { fieldId } = useParams();

  const calculationTypeOptions = [
    { label: t("impactPanel.total"), value: IMPACT_PANEL_CALCULATION_TYPE.TOTAL },
    { label: t("impactPanel.perHa"), value: IMPACT_PANEL_CALCULATION_TYPE.PER_HA },
  ];

  const [showPredictionInfoDrawer, setShowPredictionInfoDrawer] = useState(false);
  const [showMetricInfoDrawer, setShowMetricInfoDrawer] = useState(false);
  const [showBaselineInfoDrawer, setShowBaselineInfoDrawer] = useState(false);
  const [showCalculationsExplainerDrawer, setShowCalculationsExplainerDrawer] = useState(false);
  const [showPersonalizeInsightsDrawer, setShowPersonalizeInsightsDrawer] = useState(false);
  // for the AFV, we only show the per ha option because the data aggregation for the total is not consistent when fields have partial data for different years
  const [calculationType, setCalculationType] = useState<IMPACT_PANEL_CALCULATION_TYPE>(
    fieldId ? IMPACT_PANEL_CALCULATION_TYPE.TOTAL : IMPACT_PANEL_CALCULATION_TYPE.PER_HA
  );

  const datasets = getEvaluationMetricDatasets(viewType, fieldId, fieldSizes, carbonProgramOutcomes, calculationType, existingCorrectionsByFieldId);
  const availableMetrics = getAvailableEvaluationMetrics(datasets);
  const availableYearsByMetric = getAvailableYearsByEvaluationMetric(datasets);
  const allYearsRangeLabel = getEvaluationMetricAllYearsLabel(availableYearsByMetric, selectedMetric);

  const [selectedYear, setSelectedYear] = useState<string>(allYearsRangeLabel);

  const isAllYearsView = Number.isNaN(Number(selectedYear));
  const metricPredictionValue = calculateMetricChangeFromBaseline(datasets, availableYearsByMetric, selectedMetric, selectedYear, isAllYearsView);

  return (
    <div className="evaluation-metrics-panel-container">
      {datasets && selectedMetric && datasets[selectedMetric] && availableMetrics.includes(selectedMetric) ? (
        <>
          <Flex justify="space-between" align="center">
            <Select
              className="payout-years-select"
              dataTestId="payout-years-select"
              name="payoutYears"
              popupMatchSelectWidth={false}
              onChange={setSelectedYear}
              defaultValue={selectedYear}
              value={selectedYear}
            >
              {[allYearsRangeLabel, ...(availableYearsByMetric[selectedMetric]?.validYears ?? [])].map((year, i) => (
                <Option data-testid={`payout-year-select-option-${i}`} value={year} key={i} className="payout-years-select-option">
                  <Flex align="center">
                    {year}
                    {Number(year) === reportingYear ? (
                      <div data-testid="reporting-year-label" className="reporting-year-label">
                        {t("reportingYear")}
                      </div>
                    ) : null}
                  </Flex>
                </Option>
              ))}
            </Select>
            <Select
              className="field-timeline-result-select"
              dataTestId="field-timeline-result-select"
              name="resultSelect"
              popupMatchSelectWidth={false}
              onChange={setCalculationType}
              defaultValue={calculationType}
              value={calculationType}
            >
              {calculationTypeOptions.map((option, i) => (
                <Option
                  data-testid={`field-timeline-result-select-option-${i}`}
                  value={option.value}
                  key={i}
                  disabled={!fieldId && option.value === IMPACT_PANEL_CALCULATION_TYPE.TOTAL}
                  className="field-timeline-result-select-option"
                >
                  {option.label}
                </Option>
              ))}
            </Select>
          </Flex>
          <span className="dotted-line-divider" />
          <div>
            <Flex justify="space-between" align="start" gap={"0.5rem"}>
              <Typography variant="paragraph-m" bold className="margin-0">
                {`${t("impactPanel.evaluationMetrics.prediction", {
                  year: isAllYearsView ? availableYearsByMetric[selectedMetric]?.lastYear : selectedYear,
                })} `}
                <IconLinkButton
                  dataTestId="evaluation-metrics-prediction-info-icon-button"
                  className="field-timeline-payout-info-icon-button width-100"
                  theme="grey"
                  iconType="info"
                  size="small"
                  onClick={() => setShowPredictionInfoDrawer(true)}
                />
              </Typography>
              {metricPredictionValue.value !== null ? (
                <Typography
                  variant="paragraph-m"
                  bold
                  className={`margin-0 output-value-string ${
                    metricPredictionValue.change === "positive"
                      ? "positive-value-format"
                      : metricPredictionValue.change === "negative"
                      ? "negative-value-format"
                      : ""
                  }`}
                >
                  {formatChangeString(metricPredictionValue.value, selectedMetric, calculationType)}
                </Typography>
              ) : null}
            </Flex>
            <span className="dotted-line-divider" />
            <Typography variant="paragraph-m" bold className=" margin-0">
              {`${t(`impactPanel.evaluationMetrics.${selectedMetric}`)} (${getEvaluationMetricsUnitOfMeasure(selectedMetric, calculationType)}) `}
              <IconLinkButton
                dataTestId="evaluation-metrics-metric-info-icon-button"
                className="field-timeline-payout-info-icon-button"
                theme="grey"
                iconType="info"
                size="small"
                onClick={() => setShowMetricInfoDrawer(true)}
              />
            </Typography>
          </div>
          <FieldTimelineLineChartWithRanges
            // use the key to force a re-render when the selected year or toggle option changes
            key={`${selectedMetric}-${selectedYear}-${calculationType}`}
            baseline={datasets[selectedMetric][0].data.avg ?? 0}
            datasets={datasets[selectedMetric]}
            reportingYear={reportingYear}
            selectedYear={selectedYear}
            unitOfMeasure={getEvaluationMetricsUnitOfMeasure(selectedMetric, calculationType)}
            reportingPeriod={reportingPeriod}
            width={320}
            height={240}
          />
          <Typography variant="paragraph-s" className="margin-0 margin-top-100 margin-bottom-100">
            {`${t("impactPanel.evaluationMetrics.baselineSetPoint", {
              baseline: formatNumber({
                number: 0,
                decimalPlaces: 2,
              }),
              unitOfMeasure: getEvaluationMetricsUnitOfMeasure(selectedMetric, calculationType),
            })}`}
            <IconLinkButton
              dataTestId="evaluation-metrics-baseline-info-icon-button"
              className="field-timeline-payout-info-icon-button"
              theme="grey"
              iconType="info"
              size="small"
              onClick={() => setShowBaselineInfoDrawer(true)}
            />
          </Typography>
          <CalculationsExplainer t={t} setShowCalculationsExplainerDrawer={setShowCalculationsExplainerDrawer} />
          {/* TODO: restore this when the new feature designs will be ready */}
          {/* {fieldId && selectedMetric === EVALUATION_METRICS.HUMUS && (
            <EvaluationMetricsCorrectionsPanel
              t={t}
              setShowPersonalizeInsightsDrawer={setShowPersonalizeInsightsDrawer}
              availableYears={availableYearsByMetricForCorrections[selectedMetric] ?? []}
              fieldId={fieldId}
              existingCorrections={existingCorrectionsByFieldId[fieldId]?.[selectedMetric]}
            />
          )} */}
        </>
      ) : (
        <EmptyResultCard t={t} />
      )}
      <ExtraInfo setVisible={setShowPredictionInfoDrawer} type="evaluation-metrics-prediction-info" visible={showPredictionInfoDrawer} />
      <ExtraInfo setVisible={setShowMetricInfoDrawer} type={`evaluation-metrics-${selectedMetric}-info`} visible={showMetricInfoDrawer} />
      <ExtraInfo setVisible={setShowBaselineInfoDrawer} type="evaluation-metrics-baseline-info" visible={showBaselineInfoDrawer} />
      <ExtraInfo setVisible={setShowCalculationsExplainerDrawer} type="evaluation-metrics-calculations-explainer" visible={showCalculationsExplainerDrawer} />
      <ExtraInfo setVisible={setShowPersonalizeInsightsDrawer} type="evaluation-metrics-personalize-insights" visible={showPersonalizeInsightsDrawer} />
    </div>
  );
};

const EmptyResultCard = ({ t }: { t: TFunction }) => (
  <div data-testid="evaluation-metrics-card-empty" className="field-timeline-result-card">
    <div className="field-timeline-result-card container empty">
      <Flex vertical={true} align="center" justify="center" style={{ height: "100%" }}>
        <Icon dataTestId="evaluation-metrics-card-empty-icon" className="margin-bottom-100" iconName="klim/crop-footprint" size="xl" />
        <Typography variant="paragraph-m" data-testid="evaluation-metrics-card-empty-title" className="field-timeline-result-card-empty-title">
          {t("impactPanel.evaluationMetrics.emptyResultCard.title")}
        </Typography>
        <Typography
          variant="paragraph-m"
          data-testid="evaluation-metrics-card-empty-description"
          className="field-timeline-result-card-empty-payout-description"
        >
          {t("impactPanel.evaluationMetrics.emptyResultCard.description")}
        </Typography>
      </Flex>
    </div>
  </div>
);

const CalculationsExplainer = ({ t, setShowCalculationsExplainerDrawer }: { t: TFunction; setShowCalculationsExplainerDrawer: (visible: boolean) => void }) => (
  <AlertBanner
    dataTestId="calculations-explainer-container"
    type="info"
    iconName="tabler/calculator"
    iconSize={24}
    descriptionSize="paragraph-m"
    description={t("impactPanel.evaluationMetrics.calculationsExplainer.text")}
    link
    linkText={t("impactPanel.evaluationMetrics.calculationsExplainer.buttonText")}
    onClick={() => setShowCalculationsExplainerDrawer(true)}
  />
);
